import React, { useState } from "react";
import Checkbox from "components/checkbox";
import { toast } from "react-toastify";
import { axiosApi } from "config";
import { useDispatch } from "react-redux";
import { login } from "store/slices/auth";
import { useNavigate, Link } from "react-router-dom";
import { ButtonToolbar, Form, InputGroup } from "rsuite";
import GoogleAuth from "components/googleAuth/GoogleAuth"; 

export default function SignIn() {
  const [loading, setLoading] = useState(false);
  const [pwdVisible, setPwdVisible] = useState(false);
  const [remember, setRemember] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChangePasswordView = () => {
    setPwdVisible(!pwdVisible);
  };

  const handleSubmit = async (data) => {
    if (data.password.length < 8) {
      toast.warn("The password should be more than 8 characters.");
      return false;
    }
    try {
      setLoading(true);
      const res = await axiosApi.post("/auth/login", data);

      const { platforms, data: loginData } = res.data;
      const { token, user } = loginData;

      // Set auth token and save to localStorage
      axiosApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      if (remember) {
        localStorage.setItem("Riplestack_info", token);
      }
      
      // Update Redux state
      dispatch(login({ platforms, token, user }));
      
      // Check if there was a selected plan before sign-in
      const selectedPlan = localStorage.getItem('selectedPlan');
      if (selectedPlan) {
        const plan = JSON.parse(selectedPlan);
        
        try {
          const { data } = await axiosApi.post("/api/subscription/create-checkout-session", {
            plan: plan.name,
            priceId: plan.priceId,
            billingCycle: plan.billingCycle,
            trialPeriodDays: 14,
            userId: user.id,
            customerEmail: user.email
          }, { withCredentials: true });
          
          // Clear the stored plan
          localStorage.removeItem('selectedPlan');
          
          // Redirect to Stripe Checkout
          window.location.href = data.url;
        } catch (error) {
          console.error('Error creating checkout session:', error);
          toast.error('Failed to initiate subscription. Please try again from the pricing page.');
          navigate("/main/default");
        }
      } else {
        toast.success("Logged in successfully!");
        // Make sure to wait for the toast to be visible before navigating
        setTimeout(() => {
          navigate("/main/default");
        }, 500);
      }

    } catch (err) {
      const msg = err?.response?.data?.statusMsg || "Please check your credentials";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-[32px] lg:mb-10 lg:items-center lg:justify-end">
      {/* Sign in section */}
      <div className="mt-[10vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-normal text-navy-700 dark:text-white">
          Welcome to <span className="font-bold">Riplestack</span>
        </h4>
        <p className="mb-9 ml-1 text-base text-gray-600">
          Ready to manage all your social media accounts from one place?
        </p>
        <Form fluid onSubmit={handleSubmit}>
          <Form.Group controlId="email">
            <Form.ControlLabel>Email*</Form.ControlLabel>
            <Form.Control 
              placeholder="name@email.com" 
              required 
              name="email" 
              type="email"
            />
          </Form.Group>
          <Form.Group controlId="password">
            <Form.ControlLabel>Password*</Form.ControlLabel>
            <InputGroup inside>
              <Form.Control 
                required 
                name="password" 
                placeholder="Min. 8 characters" 
                type={pwdVisible ? 'text' : 'password'} 
              />
              <InputGroup.Button onClick={handleChangePasswordView}>
                {pwdVisible ? 'Hide' : 'Show'}
              </InputGroup.Button>
            </InputGroup>
          </Form.Group>
          {/* Checkbox */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center">
              <Checkbox 
                name="remember" 
                onChange={(e) => setRemember(e.target.checked)}
                checked={remember}
              />
              <p className="ml-2 text-sm font-medium text-navy-700 dark:text-white">
                Remember me
              </p>
            </div>
            <Link
              to="/auth/forgot-password"
              className="text-sm font-medium text-[#8912E7] hover:text-brand-600 dark:text-white"
            >
              Forgot Password?
            </Link>
          </div>
          <Form.Group>
            <ButtonToolbar>
              <button 
                type="submit" 
                disabled={loading}
                className="relative flex items-center justify-center bg-gradient-to-b from-[#7F03E2] to-[#A13BF2] px-[40px] linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
              >
                {loading ? (
                  <svg 
                    aria-hidden="true" 
                    className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" 
                    viewBox="0 0 100 101" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                  </svg>
                ) : (
                  "Sign In"
                )}
              </button>
            </ButtonToolbar>
          </Form.Group>
        </Form>
        <p className="mb-9 mt-3 ml-1 text-base text-gray-600">
          Don't have an account yet? <Link to="/auth/register" className="text-[#8912E7] hover:text-brand-600">Sign Up</Link>
        </p>
        <div className="mt-3 mb-6 flex items-center gap-3">
          <div className="h-px w-full bg-gray-200 dark:bg-navy-700" />
          <p className="text-base text-gray-600 dark:text-white"> or </p>
          <div className="h-px w-full bg-gray-200 dark:bg-navy-700" />
        </div>
        {/* Google Authentication Button */}
        <div className="w-full flex justify-center">
          <GoogleAuth />
        </div>
      </div>
    </div>
  );
}
